import { useMatch, useNavigate } from "common/router/hooks";
import { parseNumber } from "common/utils/numbers";
import { stringToValidValue } from "common/utils/strings";
import Navigate from "common/router/Navigate";

import { Paths, urls } from "client/routes";
import { useClientQuery } from "client/accounts/hooks";

import { AppointmentBookingStep } from "../types";
import AppointmentBookingSteps, { AppointmentBookingStepProps } from "./AppointmentBookingSteps";
import AppointmentBookingAuthenticationProvider from "../providers/AppointmentBookingAuthenticationProvider";
import { AppointmentBookingStepSet, BOOKING_STEPS } from "../constants/booking";
import { useAppointmentsQuery, useCalendarInfoQuery } from "../hooks";
import AppointmentBookingPage from "./AppointmentBookingPage";
import { useDefaultCalendarId } from "../hooks/data";

const stepToComponent: Record<
  AppointmentBookingStep,
  React.ComponentType<AppointmentBookingStepProps>
> = {
  [AppointmentBookingStep.Start]: AppointmentBookingSteps.Start,
  [AppointmentBookingStep.Pets]: AppointmentBookingSteps.Pets,
  [AppointmentBookingStep.Reason]: AppointmentBookingSteps.Reason,
  [AppointmentBookingStep.DateTime]: AppointmentBookingSteps.DateTime,
  [AppointmentBookingStep.Review]: AppointmentBookingSteps.Review,
};

const AppointmentBooking = () => {
  const calendar = useDefaultCalendarId();
  const match = useMatch<Paths["scheduler"]["book"]>();
  const step = stringToValidValue(
    match.params.step,
    AppointmentBookingStepSet,
    AppointmentBookingStep.Start,
  );

  const appointmentIds = (match.search.appointment_ids ?? "")
    .split(",")
    .filter(Boolean)
    .map(parseNumber);

  const {
    data: appointments = [],
    isLoading,
    error,
  } = useAppointmentsQuery(
    { appointment_ids: appointmentIds },
    { enabled: appointmentIds.length > 0 },
  );

  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();
  const { data: info, isLoading: isInfoLoading, error: infoError } = useCalendarInfoQuery(calendar);

  const bookableKinds = info?.enabled_appointment_kinds.filter((kind) => kind.is_client_bookable);

  const navigate = useNavigate();
  const navigateToStep = (s: AppointmentBookingStep) => {
    navigate(
      urls.scheduler.book({
        params: { step: s },
        search: appointmentIds.length ? { appointment_ids: appointmentIds.join(",") } : undefined,
      }),
    );
  };

  const stepIndex = BOOKING_STEPS.indexOf(step);

  const onPrevious = () => {
    if (stepIndex > 0) {
      navigateToStep(BOOKING_STEPS[stepIndex - 1]);
    } else {
      navigate(urls.home.root());
    }
  };

  const onNext = () => {
    if (stepIndex < BOOKING_STEPS.length - 1) {
      navigateToStep(BOOKING_STEPS[stepIndex + 1]);
    } else {
      navigate(
        urls.scheduler.bookConfirm({ search: { appointment_ids: appointmentIds.join(",") } }),
      );
    }
  };

  if (isLoading || isClientLoading || isInfoLoading) {
    return <AppointmentBookingPage.Loading onPrevious={onPrevious} />;
  }

  if (error || clientError || infoError) {
    return <AppointmentBookingPage.Error onPrevious={onPrevious} />;
  }

  if (!bookableKinds?.length || !info?.is_client_booking_enabled) {
    return (
      <AppointmentBookingPage.Error
        onPrevious={onPrevious}
        description="Appointment booking is unavailable at this time"
      />
    );
  }

  // Ensure the steps that require appointments have them
  if (
    !isLoading &&
    !appointments.length &&
    !(step === AppointmentBookingStep.Start || step === AppointmentBookingStep.Pets)
  ) {
    return (
      <Navigate
        dest={urls.scheduler.book({
          params: { step: AppointmentBookingStep.Start },
          search: { is_expired: true },
        })}
      />
    );
  }

  const Component = stepToComponent[step];
  return (
    <AppointmentBookingAuthenticationProvider step={step}>
      <Component
        onNext={onNext}
        onPrevious={onPrevious}
        appointments={appointments}
        info={info}
        client={client}
      />
    </AppointmentBookingAuthenticationProvider>
  );
};

export default AppointmentBooking;
